import { DataTypeList, Labels } from "../../../../../features/data-types"
import { Action, Column, Data, FieldType, FieldValue, ModalId, SpinnerDiv, UIPermission, useUIActions, Badge, O } from "../../../../../features/ui"
import { useSystemStateHooks } from "../../../../system"
import { define } from "../../../../../utils/typeUtils"
import { CashFund, CashFundType } from "../../../state/types"
import { DataType, TableName } from "../../../../../tables"
import { CashFundFilters } from "./cashFundFilters"
import { useState } from "react"
import { useLocation } from "react-router-dom"
import { RoutePath } from "../../../../../features/routes"
import { useStateHooks } from "../../../../../state"

type Props = {
    cashFunds: CashFund[]
}

export const CashFundList = (props: Props) => {
    const { cashFunds } = props

    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [currentCashFund, setCurrentCashFund] = useState<CashFund>()

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_CASH_FUNDS)
    const { find } = useStateHooks()
    const { pathname } = useLocation()

    const { toggleModal } = useUIActions()

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'type',
            type: FieldType.TEXT,
            label: 'Tipo',
            sortValue: (value?: FieldValue) => Labels.cashFundTypeLabel(value as CashFundType),
            render: (value?: FieldValue) => <Badge label={Labels.cashFundTypeLabel(define(value) as CashFundType)} />
        },
        {
            name: 'availableAmount',
            type: FieldType.PRICE,
            label: 'Disponible'
        },
        {
            name: 'reservedAmount',
            type: FieldType.PRICE,
            label: 'Reservado'
        }
    ]

    const onRemove = (id?: string) => {
        setCurrentCashFund(find(TableName.CASH_FUNDS, id) as O<CashFund>)
        toggleModal(ModalId.REMOVE)
    }

    const actions = (id: string): Action[] => {
        const actionList: Action[] = []
        if (!readOnly) {
            actionList.push(
                { icon: 'plus-circle-fill', tooltip: 'Agregar Dinero', href: `${pathname}/${id}/${RoutePath.ADD}` },
                { icon: 'dash-circle-fill', kind: 'secondary', tooltip: 'Retirar Dinero', href: `${pathname}/${id}/${RoutePath.SUBSTRACT}` },
                { icon: 'arrow-left-right', tooltip: 'Transferir Dinero', href: `${pathname}/${id}/${RoutePath.TRANSFER}` },
                { icon: 'pencil-fill', tooltip: 'Editar', href: `${pathname}/${id}/${RoutePath.EDIT}` },
                { icon: 'trash-fill', kind: 'secondary', tooltip: 'Eliminar', onClick: onRemove }
            )
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        setRows(dataTypes as CashFund[])
        setAsyncDataLoaded(true)
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <CashFundFilters
                cashFunds={cashFunds}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    currentDataTypeParam={currentCashFund}
                    actions={actions}
                    readOnly={readOnly}
                />
            )}
        </>
    )
}
